(() => {
class FsAdaptiveMedia extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-fs-media-toggle]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.saveData = Boolean(navigator.connection?.saveData);
    this.mobile = window.matchMedia('(max-width: 749px)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && (!this.mobile || this.dataset.autoplayMobile === 'true');
    this.onToggle = this.onToggle.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);

    this.dataset.videoState = 'poster';
    this.dataset.dataSaver = String(this.saveData);
    this.toggle?.addEventListener('click', this.onToggle);
    this.reducedMotion.addEventListener?.('change', this.onMediaChange);
    document.addEventListener('shopify:section:select', this.onSectionSelect);

    if (!this.video || this.saveData || !this.autoplay) return;
    if (this.dataset.priority === 'true') {
      requestAnimationFrame(() => requestAnimationFrame(() => this.prepare()));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.prepare();
        this.observer?.disconnect();
      },
      { rootMargin: '600px 0px' },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.pause();
    this.observer?.disconnect();
    this.toggle?.removeEventListener('click', this.onToggle);
    this.reducedMotion?.removeEventListener?.('change', this.onMediaChange);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
  }

  prepare(forcePlayback = false) {
    if (!this.video || this.saveData) return;
    if (!this.video.querySelector('source')) this.attachSources();
    if (forcePlayback || (this.autoplay && !this.reducedMotion.matches)) this.play();
  }

  attachSources() {
    const mobileTemplate = this.querySelector('template[data-fs-mobile-sources]');
    const desktopTemplate = this.querySelector('template[data-fs-desktop-sources]');
    const useMobile = this.mobile && mobileTemplate?.content.children.length;
    const template = useMobile ? mobileTemplate : desktopTemplate;
    if (!template) return;
    this.video.append(template.content.cloneNode(true));
    this.video.load();
    this.dataset.videoState = 'ready';
  }

  async play() {
    if (!this.video) return;
    try {
      await this.video.play();
      this.dataset.videoState = 'playing';
      this.toggle?.setAttribute('aria-label', this.toggle.dataset.pauseLabel);
    } catch (_error) {
      this.dataset.videoState = 'blocked';
      this.toggle?.setAttribute('aria-label', this.toggle.dataset.playLabel);
    }
  }

  pause() {
    this.video?.pause();
    if (this.dataset.videoState === 'playing') this.dataset.videoState = 'paused';
    this.toggle?.setAttribute('aria-label', this.toggle.dataset.playLabel);
  }

  onToggle() {
    if (!this.video?.querySelector('source')) {
      this.prepare(true);
      return;
    }
    if (this.video.paused) this.play();
    else this.pause();
  }

  onMediaChange(event) {
    if (event.matches) this.pause();
    else if (this.autoplay) this.play();
  }

  onSectionSelect(event) {
    if (event.target.contains(this)) this.pause();
  }
}

if (!customElements.get('fs-adaptive-media')) {
  customElements.define('fs-adaptive-media', FsAdaptiveMedia);
}

class FsControlledVideo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onPlaybackIntent = this.onPlaybackIntent.bind(this);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    this.video?.addEventListener('pointerdown', this.onPlaybackIntent);
    this.video?.addEventListener('keydown', this.onPlaybackIntent);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.hydrate();
      },
      { rootMargin: '600px 0px' },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.video?.removeEventListener('pointerdown', this.onPlaybackIntent);
    this.video?.removeEventListener('keydown', this.onPlaybackIntent);
  }

  hydrate() {
    if (!this.video || this.dataset.hydrated === 'true') return;
    if (this.dataset.poster) this.video.poster = this.dataset.poster;
    this.dataset.hydrated = 'true';
    this.observer?.disconnect();
  }

  preparePlayback() {
    if (!this.video || this.video.querySelector('source')) return;
    this.hydrate();
    const template = this.querySelector('template[data-fs-video-sources]');
    if (template) this.video.append(template.content.cloneNode(true));
    this.video.load();
  }

  onPlaybackIntent(event) {
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
    this.preparePlayback();
  }

  onSectionSelect(event) {
    if (event.target.contains(this)) this.hydrate();
  }
}

if (!customElements.get('fs-controlled-video')) {
  customElements.define('fs-controlled-video', FsControlledVideo);
}
})();
